import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// ─── EXPORTAR HISTORIA CLÍNICA A WORD ───────────────────────────────────────

export async function exportarHistoriaClinica(paciente, antecedentes, visitas, clinicaData) {
  const nombreClinica = clinicaData?.nombre || 'Clínica';
  const direccion = clinicaData?.direccion ? `${clinicaData.direccion}, ${clinicaData.ciudad || ''}` : '';

  const campo = (label, valor) => valor ? `<p><strong>${label}:</strong> ${valor}</p>` : '';

  const visitasHTML = (visitas || []).map((v, i) => {
    const fecha = v.fecha?.toDate ? v.fecha.toDate().toLocaleDateString('es-AR') : v.fecha || '—';
    return `
      <h3 style="color:#2B4A8F;margin-top:24px;">Visita ${i + 1} — ${fecha}</h3>
      ${campo('Motivo', v.motivoVisita)}
      ${campo('AV OD SC', v.avODSC)} ${campo('AV OI SC', v.avOISC)}
      ${campo('AV OD CC', v.avODCC)} ${campo('AV OI CC', v.avOICC)}
      ${campo('PIO OD', v.pioOD ? v.pioOD + ' mmHg' : null)}
      ${campo('PIO OI', v.pioOI ? v.pioOI + ' mmHg' : null)}
      ${campo('Biomicroscopía OD', v.biomOD)}
      ${campo('Biomicroscopía OI', v.biomOI)}
      ${campo('Fondo OD', v.fondoOD)}
      ${campo('Fondo OI', v.fondoOI)}
      ${campo('Diagnóstico', v.diagnostico)}
      ${campo('Tratamiento', v.tratamiento)}
      ${campo('Observaciones', v.observaciones)}
      <hr style="border:0.5px solid #D0D8E8;margin:16px 0;">
    `;
  }).join('');

  const html = `
    <!DOCTYPE html>
    <html>
    <head><meta charset="utf-8"><style>
      body { font-family: Arial, sans-serif; color: #1A2740; font-size: 13px; margin: 40px; }
      h1 { color: #223A5E; font-size: 20px; text-align: center; }
      h2 { color: #223A5E; font-size: 16px; border-bottom: 1px solid #D0D8E8; padding-bottom: 8px; margin-top: 24px; }
      h3 { color: #2B4A8F; font-size: 14px; }
      p { margin: 4px 0; line-height: 1.6; }
      .header { text-align: center; margin-bottom: 32px; border-bottom: 2px solid #223A5E; padding-bottom: 16px; }
      .footer { margin-top: 40px; font-size: 11px; color: #8A9BB5; text-align: right; }
    </style></head>
    <body>
      <div class="header">
        <h1>${nombreClinica}</h1>
        ${direccion ? `<p style="color:#8A9BB5;">${direccion}</p>` : ''}
        <h2 style="border:none;text-align:center;">HISTORIA CLÍNICA</h2>
      </div>

      <h2>Datos del paciente</h2>
      ${campo('Nombre completo', `${paciente.apellido || ''}, ${paciente.nombre || ''}`)}
      ${campo('DNI', paciente.dni)}
      ${campo('Fecha de nacimiento', paciente.fechaNacimiento)}
      ${campo('Teléfono', paciente.telefono)}
      ${campo('Email', paciente.email)}
      ${campo('Cobertura', paciente.cobertura)}
      ${campo('Dirección', paciente.direccion)}

      <h2>Antecedentes</h2>
      ${campo('Antecedentes personales', antecedentes?.antPersonales)}
      ${campo('Antecedentes familiares', antecedentes?.antFamiliares)}
      ${campo('Alergias', antecedentes?.alergias)}
      ${campo('Medicación habitual', antecedentes?.medicacion)}
      ${campo('Observaciones', antecedentes?.observaciones)}

      ${visitas && visitas.length > 0 ? `<h2>Visitas (${visitas.length})</h2>${visitasHTML}` : ''}

      <div class="footer">Documento generado el ${new Date().toLocaleDateString('es-AR')}</div>
    </body>
    </html>
  `;

  const htmlDocx = await import('html-docx-js/dist/html-docx');
  const converted = htmlDocx.default.asBlob(html);
  saveAs(converted, `HC_${paciente.apellido}_${paciente.nombre}_${new Date().toISOString().split('T')[0]}.docx`);
}

// ─── EXPORTAR MOVIMIENTOS DE CAJA A EXCEL ───────────────────────────────────

export async function exportarMovimientosCaja(movimientos, periodo, clinicaData) {
  const nombreClinica = clinicaData?.nombre || 'Clínica';

  const filas = movimientos.map(m => {
    const fecha = m.fecha?.toDate ? m.fecha.toDate().toLocaleDateString('es-AR') : '—';
    return {
      'Fecha': fecha,
      'Tipo': m.tipo === 'ingreso' ? 'Ingreso' : 'Egreso',
      'Categoría': m.categoria || '—',
      'Detalle': m.detalle || '—',
      'Paciente / Proveedor': m.paciente || m.proveedor || '—',
      'Médico': m.medico || '—',
      'Monto': m.monto || 0,
      'Moneda': m.moneda || 'ARS',
      'Forma de pago': m.formaPago || '—',
      'Estado': m.estado || '—',
      'Registrado por': m.registradoPor || '—',
    };
  });

  const totalIngresosARS = movimientos.filter(m => m.tipo === 'ingreso' && m.moneda === 'ARS').reduce((a, m) => a + (m.monto || 0), 0);
  const totalEgresosARS = movimientos.filter(m => m.tipo === 'egreso' && m.moneda === 'ARS').reduce((a, m) => a + (m.monto || 0), 0);
  const totalIngresosUSD = movimientos.filter(m => m.tipo === 'ingreso' && m.moneda === 'USD').reduce((a, m) => a + (m.monto || 0), 0);
  const totalEgresosUSD = movimientos.filter(m => m.tipo === 'egreso' && m.moneda === 'USD').reduce((a, m) => a + (m.monto || 0), 0);

  filas.push({});
  filas.push({ 'Fecha': 'RESUMEN', 'Tipo': '', 'Categoría': '', 'Detalle': '', 'Paciente / Proveedor': '', 'Médico': '', 'Monto': '', 'Moneda': '', 'Forma de pago': '', 'Estado': '', 'Registrado por': '' });
  filas.push({ 'Fecha': 'Ingresos ARS', 'Monto': totalIngresosARS, 'Moneda': 'ARS' });
  filas.push({ 'Fecha': 'Egresos ARS', 'Monto': totalEgresosARS, 'Moneda': 'ARS' });
  filas.push({ 'Fecha': 'Resultado ARS', 'Monto': totalIngresosARS - totalEgresosARS, 'Moneda': 'ARS' });
  filas.push({ 'Fecha': 'Ingresos USD', 'Monto': totalIngresosUSD, 'Moneda': 'USD' });
  filas.push({ 'Fecha': 'Egresos USD', 'Monto': totalEgresosUSD, 'Moneda': 'USD' });
  filas.push({ 'Fecha': 'Resultado USD', 'Monto': totalIngresosUSD - totalEgresosUSD, 'Moneda': 'USD' });

  const ws = XLSX.utils.json_to_sheet(filas);
  ws['!cols'] = [{ wch: 12 }, { wch: 10 }, { wch: 20 }, { wch: 30 }, { wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 8 }, { wch: 15 }, { wch: 15 }, { wch: 25 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Movimientos');

  const nombreArchivo = `Caja_${nombreClinica.replace(/\s+/g, '_')}_${periodo || new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(wb, nombreArchivo);
}